let encryptionKey = null;

const generateEncryptionKey = async () => {
    if (!encryptionKey) {
        // 먼저 localStorage에서 기존 키를 확인
        const savedKeyData = localStorage.getItem('encryption_key');
        
        if (savedKeyData) {
            try {
                const keyData = JSON.parse(savedKeyData);
                encryptionKey = await window.crypto.subtle.importKey(
                    'raw',
                    new Uint8Array(keyData),
                    { name: "AES-GCM", length: 256 },
                    true,
                    ["encrypt", "decrypt"]
                );
            } catch (error) {
                console.warn('Failed to import saved encryption key, generating new one:', error);
                // 저장된 키가 손상된 경우 새로 생성
                await createAndSaveNewKey();
            }
        } else {
            // 저장된 키가 없는 경우 새로 생성
            await createAndSaveNewKey();
        }
    }
    return encryptionKey;
};

const createAndSaveNewKey = async () => {
    encryptionKey = await window.crypto.subtle.generateKey(
        { name: "AES-GCM", length: 256 },
        true,
        ["encrypt", "decrypt"]
    );
    
    // 키를 export하여 localStorage에 저장
    const exportedKey = await window.crypto.subtle.exportKey('raw', encryptionKey);
    const keyData = Array.from(new Uint8Array(exportedKey));
    localStorage.setItem('encryption_key', JSON.stringify(keyData));
};

const encryptToken = async (token) => {
    const key = await generateEncryptionKey();
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const tokenData = new TextEncoder().encode(token);

    const encryptedData = await window.crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        key,
        tokenData
    );

    return {
        iv: Array.from(iv),
        encryptedToken: Array.from(new Uint8Array(encryptedData))
    };
};

const decryptToken = async (encryptedData) => {
    try {
        const key = await generateEncryptionKey();
        const iv = new Uint8Array(encryptedData.iv);
        const encryptedToken = new Uint8Array(encryptedData.encryptedToken);

        const decryptedData = await window.crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            key,
            encryptedToken
        );

        return new TextDecoder().decode(decryptedData);
    } catch (error) {
        console.error('Failed to decrypt token:', error);
        // 복호화 실패 시 토큰과 암호화 키를 모두 삭제
        removeToken();
        localStorage.removeItem('encryption_key');
        encryptionKey = null;
        return null;
    }
};

export const setToken = async (token) => {
    try {
        const encryptedData = await encryptToken(token);
        localStorage.setItem('encrypted_token', JSON.stringify(encryptedData));
        return true;
    } catch (error) {
        console.error('Failed to save token:', error);
        return false;
    }
};

export const getToken = async () => {
    try {
        const encryptedData = JSON.parse(localStorage.getItem('encrypted_token'));
        if (!encryptedData) return null;

        return await decryptToken(encryptedData);
    } catch (error) {
        console.error('Failed to retrieve token:', error);
        // 토큰 데이터가 손상된 경우 삭제
        removeToken();
        return null;
    }
};

export const removeToken = () => {
    localStorage.removeItem('encrypted_token');
    localStorage.removeItem('token_expire_time_utc');
    // 로그아웃 시에는 암호화 키도 함께 삭제
    localStorage.removeItem('encryption_key');
    encryptionKey = null;
};

/** 로그인 응답의 expire_time_at_utc(ISO 8601) 저장 */
export const setTokenExpiry = (expireTimeAtUtc) => {
    if (expireTimeAtUtc) {
        localStorage.setItem('token_expire_time_utc', expireTimeAtUtc);
    }
};

/** 저장된 만료 시각(UTC ISO 문자열) 반환, 없으면 null */
export const getTokenExpiry = () => {
    return localStorage.getItem('token_expire_time_utc');
};

/** 현재 시각이 만료 시각 이후이면 true (만료 시각이 없으면 만료로 간주하지 않음) */
export const isTokenExpired = () => {
    const expiry = getTokenExpiry();
    if (!expiry) return false;
    try {
        return new Date() >= new Date(expiry);
    } catch {
        return false;
    }
};

export const cleanupCorruptedTokens = async () => {
    try {
        const encryptedData = localStorage.getItem('encrypted_token');
        if (!encryptedData) return;

        // 토큰이 있는지 확인하고 복호화 시도
        const token = await getToken();
        if (!token) {
            // 복호화 실패 시 이미 getToken에서 정리됨
            console.log('Corrupted token cleaned up');
        }
    } catch (error) {
        console.warn('Error during token cleanup:', error);
        // 에러 발생 시 토큰과 키를 모두 삭제
        removeToken();
    }
};

export default {
    setToken,
    getToken,
    removeToken,
    setTokenExpiry,
    getTokenExpiry,
    isTokenExpired,
    cleanupCorruptedTokens
};